import React, { useContext, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { useQuickSearchQuery } from '@/api/product/quickSearch';
import { Button, SearchBar } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { Area, PageType } from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { useIsMobile } from '@/helpers';
import { useDebounce, useOnEscape, useScrollLock } from '@/helpers';
import { ArticleResults } from './ArticleResults';
import styles from './QuickSearch.module.scss';
import { Results } from './Results';

interface QuickSearchProps {
	className?: string;
	autoFocusOnStart?: boolean;
}

export const QuickSearch: React.FunctionComponent<QuickSearchProps> = ({ className, autoFocusOnStart }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: layout } = useGetLayout(segmentationId);
	const staticLinks = layout?.data.staticLinks;
	const [searchParams] = useSearchParams();

	const [inputValue, setInputValue] = React.useState(searchParams.get('phrase') ?? '');
	const debouncedInput = useDebounce(inputValue, 300);
	const [isActive, setIsActive] = React.useState(false);
	const { data: settings } = useGetSettings(segmentationId);

	const isSalesPortal = settings?.data.isSalesPortal ?? true;

	const { data: results } = useQuickSearchQuery(
		debouncedInput,
		debouncedInput.length > 0 && isActive,
		settings?.data.searchSettings,
		isSalesPortal,
	);

	const wrapperRef = React.useRef<HTMLFormElement>(null);
	const inputRef = React.useRef<HTMLInputElement>(null);

	const setScrollLock = useScrollLock();
	const navigate = useNavigate();
	const location = useLocation();
	const locationRef = React.useRef(location);

	const isMobile = useIsMobile();

	const handleFocus = React.useCallback((): void => {
		setIsActive(true);
		if (isMobile) setScrollLock(true);
	}, [isMobile, setScrollLock]);

	const resetInputValue = React.useCallback(() => {
		setInputValue(searchParams.get('phrase') ?? '');
	}, [searchParams]);

	const handleClose = React.useCallback(() => {
		if (inputRef.current) inputRef.current.blur();
		setIsActive(false);
		setScrollLock(false);
	}, [setScrollLock]);

	// Close on page change
	useEffect(() => {
		if (locationRef.current !== location) {
			locationRef.current = location;
			handleClose();
		}
	}, [location, handleClose]);

	// Reset input value on phrase change
	useEffect(() => {
		resetInputValue();
	}, [resetInputValue, searchParams]);

	useEffect(() => {
		autoFocusOnStart && inputRef?.current?.focus();
	}, [autoFocusOnStart]);

	const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
		const { value } = event.target;
		setInputValue(value);
	}, []);

	const handleSubmit = React.useCallback(
		(event: React.FormEvent): void => {
			event.preventDefault();

			const trimmedValue = inputValue.trim();

			if (trimmedValue) {
				if (inputRef.current) {
					inputRef.current.blur();
				}

				const url = staticLinks?.find((item) => item.pageType === PageType.Search)?.url;

				navigate(
					{
						pathname: url,
						search: `?phrase=${encodeURIComponent(trimmedValue)}`,
					},
					{
						state: {
							area: Area.StaticPages,
							pageType: PageType.Search,
						},
					},
				);
			}
		},
		[inputValue, navigate, staticLinks],
	);

	useOnEscape(handleClose, isActive);

	const hasProductResults =
		(results?.searchSuggestions && results.searchSuggestions.length > 0) ||
		(results?.products && results.products.length > 0);

	const hasArticleResults = results?.content && results.content.length > 0;

	return (
		<form
			className={classNames(styles.wrapper, className, {
				[styles.isActive]: isActive,
			})}
			onSubmit={handleSubmit}
			ref={wrapperRef}
		>
			<header className={styles.header}>
				<SearchBar
					inputId="quickSearch"
					value={inputValue}
					placeholder={translations?.data.shared.searchPlaceholder}
					onChange={handleChange}
					onFocus={handleFocus}
					ref={inputRef}
					variant={'inverse'}
				/>

				<Button className={styles.close} size={'sm'} variant={'ghost'} onClick={handleClose}>
					{translations?.data.shared.close}
				</Button>
			</header>

			{isActive && results && (
				<>
					{/* Key event for overlay is covered by useOnEscape */}
					{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
					<div className={styles.overlay} onClick={handleClose} />
					{hasProductResults && <Results results={results} key={results.searchSuggestions.length} />}
					{!isSalesPortal && hasArticleResults && (
						<ArticleResults results={results} key={results.content.length} />
					)}
				</>
			)}
		</form>
	);
};
